import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import StatsCard from './components/StatsCard';
import NewsletterTable from './components/NewsletterTable';
import QuickActions from './components/QuickActions';
import SubscriberPanel from './components/SubscriberPanel';
import ConfirmationModal from './components/ConfirmationModal';
import NotificationToast from './components/NotificationToast';
import newsletterService from '../../services/newsletterService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: '', id: null });
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: 'info' });
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load newsletters from Supabase on mount
  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await newsletterService?.getAll();
      setNewsletters(data);
    } catch (err) {
      setError(err?.message || 'Failed to load newsletters');
      showNotification('Failed to load newsletters', 'error');
    } finally {
      setLoading(false);
    }
  };

  const mockSubscribers = [
    {
      id: 1,
      name: "Dr. Sarah Johnson",
      email: "sarah.johnson@university.edu",
      joinedDate: "2025-11-01T10:00:00",
      organization: "State University"
    },
    {
      id: 2,
      name: "Prof. Michael Chen",
      email: "m.chen@research.org",
      joinedDate: "2025-11-03T14:30:00",
      organization: "Research Institute"
    },
    {
      id: 3,
      name: "Dr. Emily Rodriguez",
      email: "e.rodriguez@college.edu",
      joinedDate: "2025-11-05T09:15:00",
      organization: "Community College"
    },
    {
      id: 4,
      name: "James Wilson",
      email: "j.wilson@foundation.org",
      joinedDate: "2025-11-07T16:45:00",
      organization: "Private Foundation"
    },
    {
      id: 5,
      name: "Dr. Lisa Anderson",
      email: "l.anderson@institute.edu",
      joinedDate: "2025-11-10T11:20:00",
      organization: "Research Institute"
    },
    {
      id: 6,
      name: "Robert Martinez",
      email: "r.martinez@university.edu",
      joinedDate: "2025-11-12T13:00:00",
      organization: "State University"
    },
    {
      id: 7,
      name: "Dr. Jennifer Lee",
      email: "j.lee@medical.org",
      joinedDate: "2025-11-14T10:30:00",
      organization: "Medical Center"
    },
    {
      id: 8,
      name: "David Thompson",
      email: "d.thompson@agency.gov",
      joinedDate: "2025-11-16T15:15:00",
      organization: "Federal Agency"
    }
  ];

  const statsData = [
    {
      title: "Total Subscribers",
      value: "2,847",
      change: "+12.5% from last month",
      changeType: "positive",
      icon: "Users",
      iconColor: "bg-primary/10"
    },
    {
      title: "Newsletter Sent",
      value: newsletters?.filter(n => n?.status === 'published')?.length?.toString() || "0",
      change: `${newsletters?.filter(n => n?.status === 'published')?.length || 0} published`,
      changeType: "positive",
      icon: "Send",
      iconColor: "bg-success/10"
    },
    {
      title: "Avg. Open Rate",
      value: "68.4%",
      change: "+2.3% from last month",
      changeType: "positive",
      icon: "Eye",
      iconColor: "bg-accent/10"
    },
    {
      title: "Avg. Click Rate",
      value: "24.7%",
      change: "-1.2% from last month",
      changeType: "negative",
      icon: "MousePointerClick",
      iconColor: "bg-secondary/10"
    }
  ];

  const filteredNewsletters = currentFilter === 'all' 
    ? newsletters 
    : newsletters?.filter(n => n?.status === currentFilter);

  const showNotification = (message, type = 'info') => {
    setNotification({ isVisible: true, message, type });
  };

  const handleEdit = (id) => {
    navigate(`/newsletter-admin-editor/${id}`);
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      type: 'delete',
      id,
      title: 'Delete Newsletter',
      message: 'Are you sure you want to delete this newsletter? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger'
    });
  };

  const handlePublish = (id) => {
    setConfirmModal({
      isOpen: true,
      type: 'publish',
      id,
      title: 'Publish Newsletter',
      message: 'Are you sure you want to publish this newsletter?',
      confirmText: 'Publish',
      variant: 'success'
    });
  };

  const handleView = (id) => {
    const newsletter = newsletters?.find(n => n?.id === id);
    if (newsletter?.slug) {
      navigate(`/newsletter/${newsletter?.slug}`);
    }
  };

  const handleConfirmAction = async () => {
    const { type, id } = confirmModal;
    
    try {
      if (type === 'delete') {
        await newsletterService?.delete(id);
        showNotification('Newsletter deleted successfully', 'success');
        await loadNewsletters(); // Reload list
      } else if (type === 'publish') {
        await newsletterService?.publish(id);
        showNotification('Newsletter published successfully', 'success');
        await loadNewsletters(); // Reload list
      }
    } catch (err) {
      showNotification(err?.message || 'Action failed', 'error');
    }
    
    setConfirmModal({ isOpen: false, type: '', id: null });
  };

  const handleBulkAction = (action) => {
    if (action === 'export') {
      showNotification('Exporting selected newsletters...', 'info');
    } else if (action === 'publish') {
      showNotification('Publishing selected newsletters...', 'info');
    } else if (action === 'delete') {
      setConfirmModal({
        isOpen: true,
        type: 'bulk-delete',
        title: 'Delete Multiple Newsletters',
        message: 'Are you sure you want to delete the selected newsletters? This action cannot be undone.',
        confirmText: 'Delete All',
        variant: 'danger'
      });
    }
  };

  const handleExportSubscribers = () => {
    showNotification('Exporting subscriber list...', 'info');
  };

  const handleSearchSubscribers = (searchTerm) => {
    console.log('Searching subscribers:', searchTerm);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      <main className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'}`}>
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Dashboard Overview</h1>
            <p className="text-muted-foreground">Manage your newsletters and track subscriber engagement</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsData?.map((stat, index) => (
              <StatsCard key={index} {...stat} />
            ))}
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-6">Newsletter Management</h2>
            <QuickActions
              onFilterChange={setCurrentFilter}
              currentFilter={currentFilter}
              onBulkAction={handleBulkAction}
            />
            <NewsletterTable
              newsletters={filteredNewsletters}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPublish={handlePublish}
              onView={handleView}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <SubscriberPanel
                subscribers={mockSubscribers}
                onExport={handleExportSubscribers}
                onSearch={handleSearchSubscribers}
              />
            </div>
            <div className="bg-card border border-border rounded-lg shadow-elevation-sm p-6">
              <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Drafts</span>
                  <span className="text-lg font-semibold text-foreground">
                    {newsletters?.filter(n => n?.status === 'draft')?.length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Scheduled</span>
                  <span className="text-lg font-semibold text-foreground">
                    {newsletters?.filter(n => n?.status === 'scheduled')?.length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <span className="text-sm text-muted-foreground">Published</span>
                  <span className="text-lg font-semibold text-foreground">
                    {newsletters?.filter(n => n?.status === 'published')?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ConfirmationModal
        isOpen={confirmModal?.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: '', id: null })}
        onConfirm={handleConfirmAction}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
        cancelText="Cancel"
        variant={confirmModal?.variant}
      />
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        isVisible={notification?.isVisible}
        onClose={() => setNotification({ ...notification, isVisible: false })}
      />
    </div>
  );
};

export default AdminDashboard;